import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Camera, Leaf, LogOut, Package, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, avatarUrl, inr } from "@/lib/api";
import Reveal from "@/components/Reveal";
import Particles, { GradientMesh } from "@/components/Particles";

export default function Account() {
  const { user, loading, login, logout, setUser } = useAuth();
  const location = useLocation();
  const [orders, setOrders] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const effectiveUser = user || location.state?.user || null;

  useEffect(() => {
    if (effectiveUser) {
      api.getMyOrders().then(setOrders).catch(() => setOrders([]));
      api.adminCheck().then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
    }
  }, [effectiveUser]);

  if (loading && !effectiveUser) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="account-loading">
        <Leaf size={28} className="animate-pulse" style={{ color: "var(--mn-gold)" }} />
      </div>
    );
  }

  if (!effectiveUser) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4" data-testid="account-signin">
        <GradientMesh />
        <Particles count={10} />
        <Reveal className="glass-card relative w-full max-w-md rounded-[2rem] p-10 text-center">
          <p className="eyebrow">The Inner Circle</p>
          <h1 className="mt-4 font-serif text-3xl tracking-tight">
            Your rituals, <span className="gold-text italic">remembered.</span>
          </h1>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>
            Sign in with Google to see your order history, track packages faster and keep your profile photo. Guest checkout always stays available.
          </p>
          <button
            data-testid="account-google-signin"
            onClick={login}
            className="btn-gold mt-8 flex w-full items-center justify-center gap-3 rounded-full py-4 text-sm"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#070D09" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.37 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/>
            </svg>
            Continue with Google
          </button>
          <Link to="/shop" className="mt-4 inline-flex items-center gap-1.5 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text2)" }} data-testid="account-continue-guest">
            Continue as guest <ArrowRight size={13} />
          </Link>
        </Reveal>
      </div>
    );
  }

  const avatar = avatarUrl(effectiveUser.picture);

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const res = await api.uploadAvatar(f);
      setUser({ ...effectiveUser, picture: res.picture });
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-4xl overflow-hidden px-4 pb-24 pt-32 sm:px-6" data-testid="account-page">
      <Particles count={8} />
      <Reveal>
        <p className="eyebrow">My Inner Circle</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">
          Namaste, <span className="gold-text italic">{effectiveUser.name?.split(" ")[0] || "friend"}.</span>
        </h1>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-[300px_1fr]">
        <Reveal className="glass-card h-fit rounded-3xl p-7 text-center" data-testid="account-profile">
          <div className="relative mx-auto h-28 w-28">
            <div className="h-full w-full overflow-hidden rounded-full border-2" style={{ borderColor: "var(--mn-gold)" }}>
              {avatar ? (
                <img src={avatar} alt={effectiveUser.name} className="h-full w-full object-cover" data-testid="account-avatar" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-serif text-4xl" style={{ background: "var(--mn-surface)", color: "var(--mn-gold)" }} data-testid="account-avatar-initial">
                  {(effectiveUser.name || "M")[0].toUpperCase()}
                </div>
              )}
            </div>
            <button
              data-testid="avatar-upload-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              aria-label="Change profile photo"
              className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full glow-gold"
              style={{ background: "var(--mn-gold)", color: "#070D09" }}
            >
              <Camera size={15} />
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickAvatar} className="hidden" data-testid="avatar-upload-input" />
          </div>
          <p className="mt-4 font-serif text-xl" data-testid="account-name">{effectiveUser.name}</p>
          <p className="mt-1 font-mono text-xs" style={{ color: "var(--mn-text2)" }} data-testid="account-email">{effectiveUser.email}</p>
          <p className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-mint)" }}>Google verified</p>
          {isAdmin && (
            <Link to="/admin" data-testid="account-admin-link" className="btn-gold mt-3 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm">
              Owner Dashboard
            </Link>
          )}
          <button
            data-testid="account-logout"
            onClick={async () => { await logout(); toast.success("Signed out. Until next ritual."); }}
            className="btn-outline-glow mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </Reveal>

        <Reveal delay={0.1} className="glass-card rounded-3xl p-7" data-testid="account-orders">
          <p className="flex items-center gap-2 font-serif text-xl">
            <Package size={18} style={{ color: "var(--mn-gold)" }} /> Order History
          </p>
          {orders === null ? (
            <p className="mt-6 text-sm" style={{ color: "var(--mn-text2)" }}>Loading your rituals…</p>
          ) : orders.length === 0 ? (
            <div className="mt-6 text-center" data-testid="orders-empty">
              <p className="text-sm" style={{ color: "var(--mn-text2)" }}>No orders yet — your first ritual awaits.</p>
              <Link to="/shop" className="btn-gold mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm" data-testid="orders-shop-link">
                Explore the Collection <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <ul className="mt-5 space-y-3">
              {orders.map((o) => (
                <li key={o.order_id} className="flex flex-wrap items-center gap-4 rounded-2xl border p-4" style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }} data-testid={`order-row-${o.order_id}`}>
                  <div className="flex -space-x-3">
                    {o.items.slice(0, 3).map((i) => (
                      <img key={i.slug + i.variant} src={i.image} alt="" className="h-11 w-9 rounded-lg border-2 object-cover" style={{ borderColor: "var(--mn-surface)" }} />
                    ))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm" style={{ color: "var(--mn-gold)" }}>{o.order_id}</p>
                    <p className="mt-0.5 font-mono text-[0.62rem]" style={{ color: "var(--mn-text2)" }}>
                      {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {o.items.length} item(s) · {o.payment_method.toUpperCase()}
                    </p>
                  </div>
                  <span className="font-mono text-sm" style={{ color: "var(--mn-text)" }}>{inr(o.total)}</span>
                  <Link
                    to={`/track?order=${o.order_id}`}
                    data-testid={`track-link-${o.order_id}`}
                    className="btn-outline-glow flex items-center gap-1.5 rounded-full px-4 py-2 text-xs"
                  >
                    <PackageSearch size={13} style={{ color: "var(--mn-mint)" }} /> Track
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Reveal>
      </div>
    </div>
  );
}
